<template>
  <div class="chat-message-thread-item" :class="selfClass">
    <span v-if="isSent" class="secondary-text">{{ messageDetails.sender }}</span>
    <div class="chat-message-content">{{ messageDetails.textMessage }}</div>
    <div class="flex flex-row chat-thread-item-details">
      <span v-if="messageStatus" class="secondary-text">{{ messageStatus }}</span>
      <span class="secondary-text ml-1">({{ displayMedium }})</span>
    </div>
  </div>
</template>
  
<script>

import { computed } from '@vue/composition-api';

export default {
  props: {
    message: {
      type: Object,
      required: true
    },
    channel: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const messageDetails = computed(() => props.message);
    const channelDetails = computed(() => props.channel);

    const isSent = computed(() => messageDetails.value.sender !== channelDetails.value?.patientFullName);
    const isInternal = computed(() => messageDetails.value.isInternal);
    
    const senderRole = computed(() => {
      if (!isSent.value) return 'PATIENT'
      
      // Find the participant role for this message sender
      const senderParticipant = channelDetails.value?.participants?.find(p => 
        p.principal === messageDetails.value.principal
      )
      return senderParticipant?.role || 'UNKNOWN'
    })
    
    const messageStatus = computed(() => {
      if (!isSent.value) { 
        return ''
      }
      switch (messageDetails.value.inAppStatus?.toLowerCase()) {
        case 'delivered':
          return 'Delivered';
        case 'sent':
          return 'Sent';
        case 'accepted' || 'sending' || 'queued':
          return 'Sending...';
        case 'scheduled':
          return 'Scheduled';
        case 'canceled': 
          return 'Canceled';
        case 'read':
          return 'Read';
        case 'failed' || 'undelivered':
          return 'Failed';
        default:
          return '';
      }
    })
    const selfClass = computed(() => {
      const classes = []
      
      if (isSent.value) {
        classes.push('sent')
        classes.push(`role-${senderRole.value.toLowerCase()}`)
      } else {
        classes.push('received')
        classes.push('role-patient')
      }
      
      if (isInternal.value) classes.push('internal')
      if (messageStatus.value === 'Failed') classes.push('failed')
      
      return classes.join(' ')
    })
    const displayMedium = computed(() => {
      if (isInternal.value) {
        return 'internal';
      }
      switch (messageDetails.value.medium?.toLowerCase()) {
        case 'sms':
          return 'sms';
        case 'websocket':
          return 'app';
        case 'inapp':
          return 'app';
        default:
          return 'sms';
      }
    })

    return {
      messageDetails,
      channelDetails,
      isSent,
      isInternal,
      senderRole,
      messageStatus,
      selfClass,
      displayMedium,
    }
  },
}
</script>
<style lang="scss" scoped>
.chat-message-thread-item {
  display: flex;
  flex-direction: column;
  margin: 6px 0;

  .secondary-text {
    opacity: 0.5;
  }
}

.chat-message-content {
  margin: 2px 0;
  padding: 4px 10px;
  border-radius: 12px;
  width: fit-content;
  max-width: 280px;
  white-space: pre-line;
}

.sent {
  align-self: flex-end;
  .secondary-text {
    align-self: flex-end;
  }

  .chat-message-content {
    align-self: flex-end;
    background-color: #99CCFF; // Default blue for sent messages
  }

  .chat-thread-item-details {
    justify-content: flex-end;
  }
}

.received {
  .chat-message-content {
    background-color: #E8F4FD; // Light blue for patient messages
  }
}

// Role-based colors for sent messages
.sent.role-care_coordinator {
  .chat-message-content {
    background-color: #99CCFF; // Blue for care coordinators
  }
}

.sent.role-watcher {
  .chat-message-content {
    background-color: #B8E6B8; // Light green for watchers
  }
}

.sent.role-unknown {
  .chat-message-content {
    background-color: #F0F0F0; // Gray for unknown roles
  }
}

// Internal message styling (only visible to care team)
.internal {
  .chat-message-content {
    background-color: #FFF3CD !important; // Yellow background for internal
    border: 1px dashed #856404; // Dashed border
    position: relative;
    
    &::before {
      content: 'Internal';
      position: absolute;
      top: -8px;
      right: 8px;
      background-color: #856404;
      color: white;
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 3px;
      font-weight: bold;
    }
  }
}

.failed {
  .chat-message-content {
    background-color: #FFE7E6;
  }
}
</style>
